import { html } from "lit-html";
import { unsafeHTML } from "lit-html/directives/unsafe-html";
import { useState } from "Shared/haunted/CustomHooks";
import { useReduxState } from "Shared/redux/useReduxState";
import { TabName } from "./useTravelInformation";
import classNames from "classnames";
import { TravelInfoModuleItem, TravelInfoSubsection, TravelInfoWidget } from "./KenticoKontent";

export const useMobileTravelInformation = () => {
	const [personsKontent] = useReduxState("kontent.travelInformation.travelInformationPersonas.contents");
	const [portalKontent] = useReduxState("kontent.travelInformation.travelInformationCorp.contents");

	const [openedAccordion, setOpenedAccordion] = useState<string>(undefined);
	const [selectedTabName, setSelectedTabName] = useState<TabName>("people");

	const toggleAccordion = (accordionName: string) => {
		setOpenedAccordion(openedAccordion === accordionName ? undefined : accordionName);
	};

	const handleTabClick = (e: MouseEvent, name: TabName) => {
		e.preventDefault();
		e.stopPropagation();
		setSelectedTabName(name);
	};

	const listItemTemplate = (item: TravelInfoModuleItem) => html`
		<li class="flex items-center gap-x-2">
			<div class="flex h-7 w-7 items-center justify-center rounded-full bg-[#F0F3F7] text-[13px] text-n-blue">
				${unsafeHTML(item.itemIcon.value)}
			</div>
			<div class="text-sm text-n-blue">${unsafeHTML(item.itemContent.value)}</div>
		</li>
	`;

	const getHeaderTitle = (index: number, title: string) => html`
		<span class="inline-flex items-center text-sm font-black">${index}.&nbsp;${unsafeHTML(title)}</span>
	`;

	const widgetTemplate = (icon: string, title: string, url: string) => {
		const handleNavigate = () => { window.location.href = url; };

		return html`
		<div
			class="flex h-[52px] items-center justify-between rounded-lg border border-solid border-[#E0E0E0] bg-[#F7F7F7] px-3"
			@click=${handleNavigate}
		>
			<div class="flex items-center gap-x-2">
				<div class="flex h-7 w-7 items-center justify-center rounded-full bg-n-blue text-[13px] text-white">
					${unsafeHTML(icon)}
				</div>
				<div class="text-sm text-n-blue">${unsafeHTML(title)}</div>
			</div>
			<div class="h-[10px] w-[10px] rotate-45 transform border-r-2 border-t-2 border-solid border-n-blue"></div>
		</div>
	`;
	};

	const widgetsTemplate = (widgets: TravelInfoWidget[]) => html`
		<div class="grid w-full grid-cols-2 gap-2">
			${widgets.map((w) => widgetTemplate(w.icon.value, w.mobiletitle.value, w.mobileurl.value))}
		</div>
	`;

	const accordionContentTemplate = (subsection: TravelInfoSubsection) => html`
		<ul class="flex w-full flex-col gap-2">
			${subsection.modules.contents.map(listItemTemplate)}
		</ul>
	`;

	const accordionTemplate = (subsection: TravelInfoSubsection, index: number) => {
		const handleToggle = () => toggleAccordion(subsection.title.value);
		const contentTemplate = () => accordionContentTemplate(subsection);

		return html`
		<dc-accordion
			class="w-full py-4"
			.contentTemplate=${contentTemplate}
			.headerTitle=${getHeaderTitle(index + 1, subsection.title.value)}
			.isOpen=${subsection.title.value === openedAccordion}
			.onClick=${handleToggle}
		></dc-accordion>
	`;
	};

	const accordionsTemplate = (subsections: TravelInfoSubsection[]) => html`
		<div class="flex w-full flex-col">${subsections.map(accordionTemplate)}</div>
	`;

	const peopleContentTemplate = () =>
		selectedTabName === "people"
			? html` ${widgetsTemplate(personsKontent.widget)} ${accordionsTemplate(personsKontent.subsection)} `
			: "";

	const corporatePortalContentTemplate = () =>
		selectedTabName === "corporate-portal"
			? html` ${widgetsTemplate(portalKontent.widget)} ${accordionsTemplate(portalKontent.subsection)} `
			: "";

	const tabTemplate = (name: TabName, title: string) => {
		const handleClick = (e: MouseEvent) => handleTabClick(e, name);

		return html`
		<div
			class=${classNames("p-2 rounded-lg basis-1/2 cursor-pointer text-center", {
				"bg-n-blue text-white font-bold": selectedTabName === name,
				"bg-gray-300 text-gray-500 font-normal": selectedTabName !== name,
			})}
			@click=${handleClick}
		>
			${unsafeHTML(title)}
		</div>
	`;
	};

	const tabHeaderTemplate = () => html`
		<div class=${classNames("flex w-full gap-1 rounded-xl bg-gray-100 p-1")}>
			${tabTemplate("people", personsKontent.sectionTitle.value)}
			${tabTemplate("corporate-portal", portalKontent.sectionTitle.value)}
		</div>
	`;

	const htmlTemplate = () =>
		personsKontent && portalKontent
			? html`
					<div class="flex w-full flex-col items-center gap-4 px-1">
						${tabHeaderTemplate()} ${peopleContentTemplate()} ${corporatePortalContentTemplate()}
					</div>
			  `
			: "";

	return { htmlTemplate };
};
